/**
 * 平板上的 App（docs/API.md 2.3，只支持安卓平板）：接口调用和纯函数（筛选、分组、状态文字）。
 * 我的 › 学生设备里的 App 管理、日程「打开其它 App」从平板上选都用这里。
 * 列表变化时实时通道发 device_apps_changed { deviceId }，打开着的页面用 onRealtime 订阅后重新读取。
 */
import { api } from "./api";
import { ago } from "./fmt";
import type { Device, DeviceApp, DeviceAppsResponse, EventAction } from "./models";

// ---------- 接口 ----------

const appsPath = (deviceId: string) => `devices/${encodeURIComponent(deviceId)}/apps`;
const uninstallPath = (deviceId: string, pkg: string) => `${appsPath(deviceId)}/${encodeURIComponent(pkg)}/uninstall`;

/** 平板上的 App（默认带图标） */
export function fetchDeviceApps(deviceId: string, opts: { icons?: boolean; signal?: AbortSignal } = {}): Promise<DeviceAppsResponse> {
  return api.get<DeviceAppsResponse>(appsPath(deviceId), opts.icons === false ? { icons: 0 } : undefined, { signal: opts.signal });
}

/** 让平板马上重新上报；online = 平板在线（不在线时等它下次打开学生端再上报） */
export function refreshDeviceApps(deviceId: string): Promise<{ online: boolean }> {
  return api.post<{ online: boolean }>(`${appsPath(deviceId)}/refresh`);
}

/** 请求卸载：平板上弹出系统的卸载确认框，要在平板上点「确定」；系统应用 422 */
export function requestUninstall(deviceId: string, pkg: string): Promise<{ app: DeviceApp; online: boolean }> {
  return api.post<{ app: DeviceApp; online: boolean }>(uninstallPath(deviceId, pkg));
}

/** 撤回卸载请求（平板上已经弹出的确认框收不回来） */
export function cancelUninstall(deviceId: string, pkg: string): Promise<{ app: DeviceApp }> {
  return api.del<{ app: DeviceApp }>(uninstallPath(deviceId, pkg));
}

// ---------- 纯函数 ----------

/** 能管理 App 的设备：没解绑的安卓学生端 */
export function isAndroidTablet(d: Device): boolean {
  return d.kind === "student" && d.platform === "android" && d.revokedAt == null;
}

/** 按名称或包名筛选（不区分大小写，空白忽略） */
export function filterApps(apps: readonly DeviceApp[], query: string): DeviceApp[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...apps];
  return apps.filter((a) => a.label.toLowerCase().includes(q) || a.package.toLowerCase().includes(q));
}

/** 按名称排序（中文按拼音） */
export function sortApps(apps: readonly DeviceApp[]): DeviceApp[] {
  return [...apps].sort((a, b) => a.label.localeCompare(b.label, "zh-CN") || a.package.localeCompare(b.package));
}

/** 分成"可以卸载"（非系统应用）和"系统应用"两组，各自按名称排序 */
export function splitApps(apps: readonly DeviceApp[]): { removable: DeviceApp[]; system: DeviceApp[] } {
  const sorted = sortApps(apps);
  return { removable: sorted.filter((a) => !a.system), system: sorted.filter((a) => a.system) };
}

/** 日程能打开的 App：有桌面图标的，按名称排序 */
export function launchableApps(apps: readonly DeviceApp[]): DeviceApp[] {
  return sortApps(apps.filter((a) => a.launchable));
}

/** 第二行："1.2.3 · com.example.app"，没有版本时只有包名 */
export function appSubtitle(app: DeviceApp): string {
  return app.versionName ? `${app.versionName} · ${app.package}` : app.package;
}

/** 卸载状态文字；没有请求时为 null */
export function uninstallStatusText(app: DeviceApp): string | null {
  switch (app.uninstall?.status) {
    case "pending":
      return "等平板上确认";
    case "cancelled":
      return "平板上取消了";
    case "failed":
      return "卸载失败";
    default:
      return null;
  }
}

/** 正在等平板上确认（显示「撤回」，否则显示「卸载」） */
export function isUninstallPending(app: DeviceApp): boolean {
  return app.uninstall?.status === "pending";
}

/** "3 分钟前上报"；从没上报过为 null */
export function reportedText(at: number | null | undefined, now = Date.now()): string | null {
  return at == null ? null : `${ago(at, now)}上报`;
}

/** 点「重新上报」后的提示 */
export function refreshToastText(online: boolean): string {
  return online ? "已通知平板重新上报" : "平板不在线，下次打开学生端时上报";
}

/** 请求卸载后的提示 */
export function uninstallToastText(online: boolean): string {
  return online ? "已发到平板，请在平板上点「确定」" : "平板不在线，下次打开学生端时弹出";
}

/** 用新返回的一行替换列表里的同一个 App */
export function replaceApp(apps: readonly DeviceApp[], app: DeviceApp): DeviceApp[] {
  return apps.map((a) => (a.package === app.package ? { ...app, icon: app.icon ?? a.icon } : a));
}

/** 日程名称最长 30 个字（与服务端一致） */
const MAX_APP_NAME = 30;

/** 从平板上选的 App → 日程的"打开其它 App"（只有安卓包名，iPad 链接留空） */
export function appAction(app: DeviceApp): Extract<EventAction, { type: "app" }> {
  const name = Array.from(app.label.trim() || app.package)
    .slice(0, MAX_APP_NAME)
    .join("");
  return { type: "app", name, iosUrl: "", androidPackage: app.package };
}
